import React, { useEffect, useState } from 'react'
import LobbyMapCards from '../LobbyMapCards'
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  Alert,
  IconButton,
  Button,
  CircularProgress,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { ChromePicker } from 'react-color'
import { Add as AddIcon, Delete as DeleteIcon, Download as DownloadIcon } from '@mui/icons-material'
import { BillingTier } from '../../../../core/models/Company'
import { useActorPermissionKeys, useSurface } from '../../../../hooks/usePermission'
import { useInviteRoles } from '../../../../hooks/useInviteRoles'
import { useRbacStore } from '../../../../stores/rbacStore'
import { rolePickerChoices, roleKeyOf, roleSendValue } from '../../utils/roleOptions'
import { CompanyFormData } from '../../types'
import { billingService, BillingTier as NewBillingTier } from '../../../../services/BillingService'
import {
  getSubscriptionStatusLabel,
  formatCurrency,
  isFreeTierCalculation,
  calculateBillingCost,
  mergeMemberCountForBilling,
} from '../../../../services/BillingUtils'
import { isValidCompanyHex } from '../../../../utils/companyBrandingPalette'
import { SEVV_SCROLLABLE_TABS_PROPS } from '../../../../theme/sevvScrollableTabsProps'
import { SEVV_TABS_OVERFLOW_SX } from '../../../../theme/sevvTabsOverflowSx'
import { alpha, useTheme } from '@mui/material/styles'
import {
  companyDialogInsetCardSx,
  companyDialogNestedPaperSx,
  paymentStatusDisplayChipSx,
  rawSubscriptionAccountStatusChipSx,
  subscriptionHistoryEventChipSx,
  subscriptionHistoryRowPaperSx,
  subscriptionStatusChipSx,
  trialUrgencyAccentColor,
  trialUrgencyChipSx,
} from './companyDialogThemeSx'
import { BillingOverrideManager } from '../billing/BillingOverrideManager'
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledButton,
} from '../../styles/StyledComponents'
import { 
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Settings as SettingsIcon,
  MonetizationOn as MonetizationIcon,
  Domain as DomainIcon,
  Wallpaper as WallpaperIcon,
} from '@mui/icons-material'
import CompanyBackgroundSettings from '../../../CompanyBackgroundSettings'

interface CompanyDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  formData: CompanyFormData
  onFormChange: (formData: CompanyFormData) => void
  mode: 'create' | 'edit'
  isGlobalAdmin?: boolean
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

type BrandingColorFormKey = 'primary_color' | 'secondary_color' | 'accent_color' | 'link_color' | 'surface_tint_color'

interface BrandingColorFieldRowProps {
  label: string
  helperText: string
  field: BrandingColorFormKey
  value: string
  pickerOpen: boolean
  onTogglePicker: () => void
  onHexChange: (hex: string) => void
}

function BrandingColorFieldRow({
  label,
  helperText,
  field,
  value,
  pickerOpen,
  onTogglePicker,
  onHexChange,
}: BrandingColorFieldRowProps) {
  const theme = useTheme()
  const trimmed = value.trim()
  const swatchBg = isValidCompanyHex(trimmed)
    ? trimmed
    : theme.palette.action.hover
  const pickerColor = isValidCompanyHex(trimmed) ? trimmed : theme.palette.grey[500]

  return (
    <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
      <Typography variant="h6" sx={{ mb: 0.5, color: 'text.primary' }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
        {helperText}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box
          sx={{
            width: 60,
            height: 40,
            backgroundColor: swatchBg,
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.primary',
            fontSize: '11px',
            fontWeight: 'bold',
            textAlign: 'center',
            px: 0.5,
          }}
          onClick={onTogglePicker}
        >
          {value.trim() ? value : 'default'}
        </Box>
        <TextField
          value={value}
          onChange={(e) => onHexChange(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
          placeholder={field === 'primary_color' || field === 'secondary_color' ? undefined : 'Leave empty for automatic'}
        />
      </Box>
      {pickerOpen && (
        <Box sx={{ position: 'absolute', zIndex: 10, mt: 0 }}>
          <ChromePicker color={pickerColor} onChange={(color) => onHexChange(color.hex)} />
        </Box>
      )}
    </Grid>
  )
}

export const CompanyDialog: React.FC<CompanyDialogProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  onFormChange,
  mode,
  isGlobalAdmin = false,
}) => {
  const theme = useTheme()
  const primaryHex = theme.palette.primary.main
  // Choosing who new sign-ups become is assigning a role (SC-439).
  const canPickDefaultRole = useSurface('company-dialog.role-picker')
  // The roles of the company being edited, one request per time the dialog opens (SC-439). A new
  // company has none yet, and when the list fails, the catalogue's system roles stand in, under
  // the names it gives: this file names no role itself.
  const actorPermissions = useActorPermissionKeys()
  const roleCompanyId = mode === 'edit' ? formData.id : undefined
  const rolesState = useInviteRoles(roleCompanyId, open && canPickDefaultRole)
  const roleCatalogue = useRbacStore((s) => s.catalogue)
  const needsRoleCatalogue =
    open && canPickDefaultRole && (!roleCompanyId || rolesState.status === 'error')
  useEffect(() => {
    if (needsRoleCatalogue) void useRbacStore.getState().ensureCatalogue()
  }, [needsRoleCatalogue])
  const defaultRoleKey = roleKeyOf(formData.default_role)
  // A non-Member default needs roles.assign and every permission of the role; Member is open to
  // anyone with roles.assign, as in cloud.
  const defaultRoleChoices = rolePickerChoices({
    roles: rolesState.roles,
    catalogueRoles: roleCatalogue?.systemRoles,
    actorPermissions,
    currentKey: defaultRoleKey,
    alwaysAllowedKey: 'member',
  })
  const defaultRolesLoading =
    !!roleCompanyId && (rolesState.status === 'loading' || rolesState.status === 'idle')
  const [activeTab, setActiveTab] = useState(0)

  // Managing the company's backgrounds is a workspace setting (SC-439). The server checks it on
  // every write; this keeps the tab from being offered to someone it would refuse.
  const canManageBackgrounds = useSurface('company-dialog.backgrounds-tab')
  // Only for a company manager editing their own company — see the Backgrounds
  // panel below for why a platform admin must not get this.
  const showBackgroundSettings = mode === 'edit' && !isGlobalAdmin && canManageBackgrounds
  // The Billing tab and its three billing reads need billing.view_plan (SC-439). Opening this
  // dialog only takes workspace.settings.manage, so without this a settings manager who may not
  // see billing got the tab and three requests the server refuses. A new company has no billing
  // data to read, so its tier fields stay.
  const canViewBilling = useSurface('company-dialog.billing-tab')
  const showBillingTab = mode === 'create' || canViewBilling
  const [brandingPickerField, setBrandingPickerField] = useState<BrandingColorFormKey | null>(null)
  const [newDomain, setNewDomain] = useState('')
  const [billingTiers, setBillingTiers] = useState<NewBillingTier[]>([])
  const [loadingTiers, setLoadingTiers] = useState(false)
  const [billingCalculation, setBillingCalculation] = useState<any>(null)
  const [loadingCalculation, setLoadingCalculation] = useState(false)
  /** Authoritative total from getCompanyUsers(..., 1, 1) envelope */
  const [parseMemberTotal, setParseMemberTotal] = useState<number | null>(null)
  /** From getComprehensiveBillingData billing_calculation.active_users when present */
  const [billingActiveUsers, setBillingActiveUsers] = useState<number | null>(null)

  const effectiveMemberCount = React.useMemo(
    () => mergeMemberCountForBilling(parseMemberTotal, billingActiveUsers),
    [parseMemberTotal, billingActiveUsers],
  )
  const [companyOverride, setCompanyOverride] = useState<any>(null)
  const [subscriptionData, setSubscriptionData] = useState<any>(null)
  const [billingHistory, setBillingHistory] = useState<any[]>([])
  const [loadingSubscriptionData, setLoadingSubscriptionData] = useState(false)
  const [loadingTrial, setLoadingTrial] = useState(false)
  const [subscriptionHistory, setSubscriptionHistory] = useState<any[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [downloadingInvoices, setDownloadingInvoices] = useState<Set<string>>(new Set())
  const [billingInfo, setBillingInfo] = useState<any>(null)
  const [loadingBillingInfo, setLoadingBillingInfo] = useState(false)
  const [editingBillingInfo, setEditingBillingInfo] = useState(false)
  const [showBillingWarning, setShowBillingWarning] = useState(false)
  const [billingFormData, setBillingFormData] = useState({
    name: '',
    email: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    },
    tax_ids: [] as Array<{ type: string; value: string }>,
    metadata: {} as Record<string, string>
  })
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [showCreateCustomerForm, setShowCreateCustomerForm] = useState(false)
  const [createCustomerFormData, setCreateCustomerFormData] = useState({
    name: '',
    email: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    }
  })

  const loadSubscriptionData = async () => {
    if (!formData.id) return
    
    try {
      setLoadingSubscriptionData(true)
      console.log('Loading subscription data for company:', formData.id)
      
      // Get comprehensive billing data including subscription info and billing history
      const result = await billingService.getComprehensiveBillingData(formData.id, {
        include_history: true,
        history_limit: 10,
        include_upcoming: true
      })
      
      console.log('Comprehensive billing data result:', result)
      
      if (result.success) {
        setSubscriptionData({
          billing_account: result.billing_account,
          subscriptions: result.subscriptions,
          upcoming_invoice: result.upcoming_invoice,
          payment_methods: result.payment_methods
        })
        setBillingHistory(result.billing_history || [])
        const au = result.billing_calculation?.active_users
        setBillingActiveUsers(typeof au === 'number' && Number.isFinite(au) ? au : null)
      } else {
        console.error('Failed to load subscription data:', result.error)
        setSubscriptionData(null)
        setBillingHistory([])
        setBillingActiveUsers(null)
      }
    } catch (error) {
      console.error('Error loading subscription data:', error)
      setSubscriptionData(null)
      setBillingHistory([])
      setBillingActiveUsers(null)
    } finally {
      setLoadingSubscriptionData(false)
    }
  }

  const loadSubscriptionHistory = async () => {
    if (!formData.id) return
    
    try {
      setLoadingHistory(true)
      const result = await billingService.getSubscriptionHistory(formData.id, 20)
      
      if (result.success) {
        setSubscriptionHistory(result.history || [])
      } else {
        console.error('Failed to load subscription history:', result.error)
        setSubscriptionHistory([])
      }
    } catch (error) {
      console.error('Error loading subscription history:', error)
      setSubscriptionHistory([])
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      setDownloadingInvoices(prev => new Set(prev).add(invoiceId))
      const result = await billingService.downloadInvoice(invoiceId)
      
      if (!result.success) {
        console.error('Failed to download invoice:', result.error)
        // You could add a toast notification here
      }
    } catch (error) {
      console.error('Error downloading invoice:', error)
    } finally {
      setDownloadingInvoices(prev => {
        const newSet = new Set(prev)
        newSet.delete(invoiceId)
        return newSet
      })
    }
  }

  const loadBillingInfo = async () => {
    if (!formData.id) return
    
    try {
      setLoadingBillingInfo(true)
      const result = await billingService.getCustomerBillingInfo(formData.id)
      
      if (result.success && result.billing_info) {
        setBillingInfo(result.billing_info)
        setBillingFormData({
          name: result.billing_info.name || '',
          email: result.billing_info.email || '',
          address: {
            line1: result.billing_info.address?.line1 || '',
            line2: result.billing_info.address?.line2 || '',
            city: result.billing_info.address?.city || '',
            state: result.billing_info.address?.state || '',
            postal_code: result.billing_info.address?.postal_code || '',
            country: result.billing_info.address?.country || ''
          },
          tax_ids: result.billing_info.tax_ids || [],
          metadata: result.billing_info.metadata || {}
        })
      } else {
        console.error('Failed to load billing info:', result.error)
        setBillingInfo(null)
      }
    } catch (error) {
      console.error('Error loading billing info:', error)
      setBillingInfo(null)
    } finally {
      setLoadingBillingInfo(false)
    }
  }

  const handleBillingInfoEdit = () => {
    setShowBillingWarning(true)
  }

  const handleBillingWarningConfirm = () => {
    setShowBillingWarning(false)
    setEditingBillingInfo(true)
  }

  const handleBillingInfoCancel = () => {
    setEditingBillingInfo(false)
    // Reset form data to original values
    if (billingInfo) {
      setBillingFormData({
        name: billingInfo.name || '',
        email: billingInfo.email || '',
        address: {
          line1: billingInfo.address?.line1 || '',
          line2: billingInfo.address?.line2 || '',
          city: billingInfo.address?.city || '',
          state: billingInfo.address?.state || '',
          postal_code: billingInfo.address?.postal_code || '',
          country: billingInfo.address?.country || ''
        },
        tax_ids: billingInfo.tax_ids || [],
        metadata: billingInfo.metadata || {}
      })
    }
  }

  const handleBillingInfoSave = async () => {
    if (!formData.id) return
    
    try {
      setLoadingBillingInfo(true)
      const result = await billingService.updateCustomerBillingInfo(formData.id, billingFormData)
      
      if (result.success && result.billing_info) {
        setBillingInfo(result.billing_info)
        setEditingBillingInfo(false)
        console.log('Billing information updated successfully')
      } else {
        console.error('Failed to update billing info:', result.error)
      }
    } catch (error) {
      console.error('Error updating billing info:', error)
    } finally {
      setLoadingBillingInfo(false)
    }
  }

  const handleCreateCustomer = async () => {
    if (!formData.id) return
    
    try {
      setCreatingCustomer(true)
      const result = await billingService.createCustomer(formData.id, createCustomerFormData)
      
      if (result.success && result.billing_info) {
        setBillingInfo(result.billing_info)
        setShowCreateCustomerForm(false)
        // Refresh subscription data to get the new customer ID
        await loadSubscriptionData()
        console.log('Stripe customer created successfully')
      } else {
        console.error('Failed to create customer:', result.error)
      }
    } catch (error) {
      console.error('Error creating customer:', error)
    } finally {
      setCreatingCustomer(false)
    }
  }

  const handleCreateCustomerCancel = () => {
    setShowCreateCustomerForm(false)
    setCreateCustomerFormData({
      name: formData.name || '',
      email: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: ''
      }
    })
  }

  // Load billing tiers when dialog opens
  React.useEffect(() => {
    if (open) {
      setActiveTab(0)
      setNewDomain('')
      setBrandingPickerField(null)
      setParseMemberTotal(null)
      setBillingActiveUsers(null)
      loadBillingTiers()
      if (mode === 'edit' && formData.id) {
        loadParseMemberTotal()
        loadCompanyOverride()
      }
    }
  }, [open])

  // Once per open, and again if the permission answer only arrives after the dialog opened.
  React.useEffect(() => {
    if (open && mode === 'edit' && formData.id && canViewBilling) {
      loadSubscriptionData()
      loadSubscriptionHistory()
    }
  }, [open, canViewBilling])

  // Load billing info when subscription data becomes available
  React.useEffect(() => {
    if (
      mode === 'edit' &&
      formData.id &&
      canViewBilling &&
      subscriptionData !== null &&
      !loadingSubscriptionData
    ) {
      // Only load billing info if we have a Stripe customer ID
      if (subscriptionData?.billing_account?.stripe_customer_id) {
        loadBillingInfo()
      } else {
        // Clear any existing local state
        setBillingInfo(null)
        setBillingFormData({
          name: '',
          email: '',
          address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postal_code: '',
            country: ''
          },
          tax_ids: [],
          metadata: {}
        })
      }
    }
  }, [subscriptionData, loadingSubscriptionData, mode, formData.id, canViewBilling])

  // Load billing calculation when billing tier or authoritative headcount changes
  React.useEffect(() => {
    if (open && mode === 'edit' && formData.id) {
      loadBillingCalculation()
    }
  }, [formData.billing_tier, parseMemberTotal, billingActiveUsers, companyOverride, subscriptionData])

  const loadCompanyOverride = async () => {
    if (!formData.id) return
    try {
      // TODO: Implement getCompanyBillingOverride in BillingService
      // const result = await billingService.getCompanyBillingOverride(formData.id)
      // if (result.success && result.override && result.override.is_active) {
      //   setCompanyOverride(result.override)
      // } else {
      //   setCompanyOverride(null)
      // }
      setCompanyOverride(null)
    } catch (error) {
      setCompanyOverride(null)
    }
  }

  const loadBillingTiers = async () => {
    try {
      setLoadingTiers(true)
      const result = await billingService.getBillingTiers(true) // Get active tiers only
      if (result.success) {
        setBillingTiers(result.billing_tiers)
      }
    } catch (error) {
      console.error('Error loading billing tiers:', error)
    } finally {
      setLoadingTiers(false)
    }
  }

  const loadParseMemberTotal = async () => {
    if (!formData.id) return

    try {
      const { CompanyService } = await import('../../../../services/CompanyService')
      const result = await CompanyService.getCompanyUsers(formData.id, undefined, true, 1, 1)
      if (result.success) {
        const n = result.total ?? result.users?.length ?? 0
        setParseMemberTotal(n)
      } else {
        console.error('Failed to load company user count:', result.error)
        setParseMemberTotal(null)
      }
    } catch (error) {
      console.error('Error loading company user count:', error)
      setParseMemberTotal(null)
    }
  }

  const loadBillingCalculation = async () => {
    if (!formData.id) return

    try {
      setLoadingCalculation(true)
      const userCount = mergeMemberCountForBilling(parseMemberTotal, billingActiveUsers)
      // Local calculation only: prefer active override, otherwise use selected tier
      // Use actual billing account tier if available, otherwise fall back to form data
      const actualTier = subscriptionData?.billing_account?.billing_tier
      const selectedTier = actualTier || billingTiers.find(tier => tier.tier_key === formData.billing_tier)

      if (companyOverride && companyOverride.is_active) {
        const freeLimit = companyOverride.custom_free_users_limit || 0
        const basePrice = companyOverride.custom_base_price || 0
        const pricePerUser = companyOverride.custom_price_per_user || 0
        const billableUsers = Math.max(0, userCount - freeLimit)
        const perUserCost = billableUsers * pricePerUser
        const preDiscount = basePrice + perUserCost
        const discountPct = companyOverride.discount_percentage || 0
        const discountAmount = preDiscount * (discountPct / 100)
        const totalMonthly = Math.max(0, preDiscount - discountAmount)

        setBillingCalculation({
          base_price: basePrice,
          price_per_user: pricePerUser,
          free_users_limit: freeLimit,
          per_user_cost: perUserCost,
          discount_percentage: discountPct,
          discount_amount: discountAmount,
          total_monthly_cost: totalMonthly,
          override_active: true,
          billable_users: billableUsers,
        })
      } else if (selectedTier) {
        const calc = calculateBillingCost(
          {
            base_price: selectedTier.base_price || 0,
            price_per_user: selectedTier.price_per_user || 0,
            free_users_limit: selectedTier.free_users_limit || 0,
          },
          userCount,
          'monthly',
        )
        setBillingCalculation({
          base_price: calc.basePrice,
          price_per_user: calc.pricePerUser,
          free_users_limit: calc.freeUsersLimit,
          per_user_cost: calc.totalUserCost,
          total_monthly_cost: calc.totalCost,
          discount_percentage: 0,
          discount_amount: 0,
          override_active: false,
          billable_users: calc.billableUsers,
        })
      } else {
        setBillingCalculation(null)
      }
    } catch (error) {
      // Silent fail – keep UI stable
      setBillingCalculation(null)
    } finally {
      setLoadingCalculation(false)
    }
  }

  const createTrialSubscription = async (trialDays: number) => {
    if (!formData.id) return
    
    try {
      setLoadingTrial(true)
      console.log('Creating trial subscription for company:', formData.id, 'for', trialDays, 'days')
      
      // Get the current billing tier - use actual billing account tier if available
      const actualTier = subscriptionData?.billing_account?.billing_tier
      const currentTier = actualTier || billingTiers.find(tier => tier.tier_key === formData.billing_tier)
      if (!currentTier) {
        console.error('No billing tier selected')
        return
      }
      
      const result = await billingService.createTrialSubscription({
        company_id: formData.id,
        tier_id: currentTier.id,
        user_count: effectiveMemberCount,
        trial_days: trialDays,
        billing_cycle: 'monthly',
        customer_email: formData.domain ? `admin@${formData.domain}` : `company-${formData.id}@trial.com`,
        customer_name: formData.name || 'Company'
      })
      
      console.log('Trial subscription result:', result)
      
      if (result.success) {
        // Refresh subscription data to show the new trial
        await loadSubscriptionData()
        console.log('Trial subscription created successfully')
      } else {
        console.error('Failed to create trial subscription:', result.error)
      }
    } catch (error) {
      console.error('Error creating trial subscription:', error)
    } finally {
      setLoadingTrial(false)
    }
  }

  const handleInputChange = (field: keyof CompanyFormData, value: any) => {
    onFormChange({
      ...formData,
      [field]: value,
    })
  }

  const handleBrandingHexChange = (field: BrandingColorFormKey, hex: string) => {
    handleInputChange(field, hex)
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleFeatureToggle = (feature: keyof CompanyFormData['features']) => {
    const currentFeatures = formData.features || {}
    handleInputChange('features', {
      ...currentFeatures,
      [feature]: !currentFeatures[feature]
    })
  }

  const handleAddDomain = () => {
    if (newDomain.trim() && !formData.allowed_domains?.includes(newDomain.trim())) {
      const currentDomains = formData.allowed_domains || []
      handleInputChange('allowed_domains', [...currentDomains, newDomain.trim()])
      setNewDomain('')
    }
  }

  const handleRemoveDomain = (domainToRemove: string) => {
    const newDomains = (formData.allowed_domains || []).filter(domain => domain !== domainToRemove)
    handleInputChange('allowed_domains', newDomains)
  }

  return (
    <>
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ color: 'primary.main', fontSize: '24px' }} />
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {mode === 'create' ? 'Create New Company' : 'Edit Company'}
          </Typography>
        </Box>
      </StyledDialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          {...SEVV_SCROLLABLE_TABS_PROPS}
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            ...SEVV_TABS_OVERFLOW_SX,
            '& .MuiTab-root': {
              color: 'text.secondary',
              minWidth: 'auto',
              maxWidth: 'none',
              whiteSpace: 'nowrap',
              '&.Mui-selected': {
                color: 'primary.main',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
            },
          }}
        >
          {/* Explicit `value` on every tab, because one of them is conditional.
              Without it MUI numbers tabs by child order, so hiding Backgrounds
              would silently shift Billing to 2 while its panel stayed at 3 —
              every tab after the gap would show the wrong content. */}
          <Tab 
            icon={<BusinessIcon />} 
            label="Basic Info" 
            value={0}
            iconPosition="start"
          />
          <Tab 
            icon={<PaletteIcon />} 
            label="Branding" 
            value={1}
            iconPosition="start"
          />
          {showBackgroundSettings && (
            <Tab
              icon={<WallpaperIcon />}
              label="Backgrounds"
              value={2}
              iconPosition="start"
            />
          )}
          {showBillingTab && (
            <Tab
              icon={<MonetizationIcon />}
              label="Billing"
              value={3}
              iconPosition="start"
            />
          )}
          <Tab 
            icon={<SettingsIcon />} 
            label="Features" 
            value={4}
            iconPosition="start"
          />
          <Tab 
            icon={<DomainIcon />} 
            label="Domain Management" 
            value={5}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <StyledDialogContent sx={{ p: 0 }}>
        {/* Basic Information Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Domain"
                value={formData.domain}
                onChange={(e) => handleInputChange('domain', e.target.value)}
                margin="dense"
                required
                helperText="e.g., mycompany.com"
              />
            </Grid>
            {mode === 'create' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  Lobby map
                </Typography>
                <LobbyMapCards value={formData.lobbyMapId || ''} onChange={(mapId) => handleInputChange('lobbyMapId', mapId)} />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Platform Name"
                value={formData.platform_name}
                onChange={(e) => handleInputChange('platform_name', e.target.value)}
                margin="dense"
                helperText="Display name for your platform"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Platform URL"
                value={formData.platform_url}
                onChange={(e) => handleInputChange('platform_url', e.target.value)}
                margin="dense"
                helperText="e.g., https://mycompany.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Users"
                type="number"
                value={formData.max_users}
                onChange={(e) => handleInputChange('max_users', parseInt(e.target.value) || 100)}
                margin="dense"
                inputProps={{ min: 1, max: 10000 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Rooms"
                type="number"
                value={formData.max_rooms}
                onChange={(e) => handleInputChange('max_rooms', parseInt(e.target.value) || 50)}
                margin="dense"
                inputProps={{ min: 1, max: 1000 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Logo URL"
                value={formData.logo_url}
                onChange={(e) => handleInputChange('logo_url', e.target.value)}
                margin="dense"
                helperText="URL to your company logo (optional)"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Branding Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <BrandingColorFieldRow
              label="Primary color"
              helperText="Main brand color for primary buttons, selected tabs, and key accents in Company Management."
              field="primary_color"
              value={formData.primary_color}
              pickerOpen={brandingPickerField === 'primary_color'}
              onTogglePicker={() =>
                setBrandingPickerField((f) => (f === 'primary_color' ? null : 'primary_color'))
              }
              onHexChange={(hex) => handleBrandingHexChange('primary_color', hex)}
            />
            <BrandingColorFieldRow
              label="Secondary color"
              helperText="Supporting brand color (contrast pair). Used where the UI needs a second brand tone."
              field="secondary_color"
              value={formData.secondary_color}
              pickerOpen={brandingPickerField === 'secondary_color'}
              onTogglePicker={() =>
                setBrandingPickerField((f) => (f === 'secondary_color' ? null : 'secondary_color'))
              }
              onHexChange={(hex) => handleBrandingHexChange('secondary_color', hex)}
            />
            <BrandingColorFieldRow
              label="Accent color"
              helperText="Highlights: info-style alerts and chips, icons, and secondary emphasis without competing with primary CTAs. Leave empty to derive from primary."
              field="accent_color"
              value={formData.accent_color}
              pickerOpen={brandingPickerField === 'accent_color'}
              onTogglePicker={() =>
                setBrandingPickerField((f) => (f === 'accent_color' ? null : 'accent_color'))
              }
              onHexChange={(hex) => handleBrandingHexChange('accent_color', hex)}
            />
            <BrandingColorFieldRow
              label="Link color"
              helperText="Hyperlinks and text links on branded surfaces. Leave empty to use a dark tone from your primary palette."
              field="link_color"
              value={formData.link_color}
              pickerOpen={brandingPickerField === 'link_color'}
              onTogglePicker={() =>
                setBrandingPickerField((f) => (f === 'link_color' ? null : 'link_color'))
              }
              onHexChange={(hex) => handleBrandingHexChange('link_color', hex)}
            />
            <BrandingColorFieldRow
              label="Surface tint"
              helperText="Very light wash for section headers and subtle panels in Company Management. Leave empty to use the default pale tint from primary."
              field="surface_tint_color"
              value={formData.surface_tint_color}
              pickerOpen={brandingPickerField === 'surface_tint_color'}
              onTogglePicker={() =>
                setBrandingPickerField((f) => (f === 'surface_tint_color' ? null : 'surface_tint_color'))
              }
              onHexChange={(hex) => handleBrandingHexChange('surface_tint_color', hex)}
            />

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" component="div">
                  These colors apply to the Company Management experience for this company (scoped branding, not the whole workspace).
                  <Box component="ul" sx={{ pl: 2, mb: 0, mt: 1 }}>
                    <li>
                      <strong>Primary</strong> and <strong>secondary</strong> drive buttons, tabs, and the main brand pair.
                    </li>
                    <li>
                      <strong>Accent</strong> shapes info alerts and secondary highlights.
                    </li>
                    <li>
                      <strong>Link</strong> sets default link color in this area.
                    </li>
                    <li>
                      <strong>Surface tint</strong> softens headers and panel backgrounds.
                    </li>
                  </Box>
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Billing Tab */}
        {/* Backgrounds — company virtual backgrounds.

            Self-contained: uploads and removals apply immediately rather than
            through this dialog's Update Company button, because a file upload has no
            meaningful draft state.

            Shown only to a manager editing their OWN company. The API scopes
            backgrounds by the caller's session, so rendering this for a platform
            admin editing someone else's company would show them their own
            company's images and quietly write to the wrong company. */}
        {showBackgroundSettings && (
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ p: 3 }}>
              <CompanyBackgroundSettings />
            </Box>
          </TabPanel>
        )}

        {showBillingTab && (
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            {/* Current Billing Tier Information */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper', backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover', backgroundImage: 'none',
                    borderColor: alpha(primaryHex, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                  mb: 3
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <MonetizationIcon sx={{ color: 'primary.main', mr: 1, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Current Billing Plan
                  </Typography>
                </Box>
                
                {(() => {
                  // Use actual billing account tier if available, otherwise fall back to form data
                  const actualTier = subscriptionData?.billing_account?.billing_tier
                  const currentTier = actualTier || billingTiers.find(tier => tier.tier_key === formData.billing_tier)
                  
                  if (!currentTier) {
                    return (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          No billing tier selected
                        </Typography>
                      </Box>
                    )
                  }
                  
                  return (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'action.hover', backgroundImage: 'none',
                            textAlign: 'center',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'background.paper', backgroundImage: 'none',
                              borderColor: alpha(primaryHex, 0.35),
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                            <Box 
                              sx={{ 
                                width: 16, 
                                height: 16, 
                                borderRadius: '50%', 
                                backgroundColor: currentTier.color || primaryHex,
                                border: '2px solid',
                                borderColor: 'divider',
                                mr: 1
                              }} 
                            />
                            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                              {currentTier.name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Current Plan
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'action.hover', backgroundImage: 'none',
                            textAlign: 'center',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'background.paper', backgroundImage: 'none',
                              borderColor: alpha(theme.palette.success.main, 0.35),
                            }
                          }}
                        >
                          <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 600, mb: 0.5 }}>
                            ${currentTier.base_price?.toFixed(2) || '0.00'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Base Price / Month
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'action.hover', backgroundImage: 'none',
                            textAlign: 'center',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'background.paper', backgroundImage: 'none',
                              borderColor: alpha(theme.palette.warning.main, 0.35),
                            }
                          }}
                        >
                          <Typography variant="h5" sx={{ color: 'warning.main', fontWeight: 600, mb: 0.5 }}>
                            ${currentTier.price_per_user?.toFixed(2) || '0.00'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Per User / Month
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  )
                })()}
              </Paper>
            </Grid>

            {/* Total Cost Calculation - Only show for existing companies */}
            {mode === 'edit' && formData.id && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                  p: 3,
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper', backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover', backgroundImage: 'none',
                    borderColor: alpha(primaryHex, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                    mb: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <MonetizationIcon sx={{ color: 'success.main', mr: 1, fontSize: 24 }} />
                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      Monthly Cost Calculation
                    </Typography>
                  </Box>
                  
                  {loadingCalculation ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} sx={{ color: 'primary.main', mr: 2 }} />
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Calculating monthly cost...
                      </Typography>
                    </Box>
                  ) : billingCalculation ? (
                    <Grid container spacing={3}>
                      {/* Company Stats */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'action.hover', backgroundImage: 'none',
                            height: '100%',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'background.paper', backgroundImage: 'none',
                              borderColor: alpha(primaryHex, 0.35),
                            }
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                            Company Statistics
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Total Users
                              </Typography>
                              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                {effectiveMemberCount}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Free Users Limit
                              </Typography>
                              <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                                {billingCalculation.free_users_limit || 0}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Billable Users
                              </Typography>
                              <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 600 }}>
                                {billingCalculation.billable_users || 0}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                      
                      {/* Total Cost Display */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'action.hover', backgroundImage: 'none',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'background.paper', backgroundImage: 'none',
                              borderColor: alpha(theme.palette.success.main, 0.35),
                            }
                          }}
                        >
                          <Typography variant="h3" sx={{ color: 'success.main', fontWeight: 'bold', mb: 1 }}>
                            {formatCurrency(billingCalculation.total_monthly_cost || 0)}
                          </Typography>
                          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                            per month
                          </Typography>
                          
                          <Typography variant="body1" sx={{ color: 'text.disabled', mb: 2 }}>
                            {formatCurrency((billingCalculation.total_monthly_cost || 0) * 12)} per year
                          </Typography>
                          
                          {billingCalculation.override_active && (
                            <Chip 
                              label="Custom Pricing Active" 
                              size="small" 
                              sx={{ 
                                bgcolor: alpha(theme.palette.info.main, 0.2),
                                color: 'info.main',
                                fontWeight: 500,
                                border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                              }} 
                            />
                          )}
                        </Paper>
                      </Grid>
                      
                      {/* Cost Breakdown */}
                      <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'action.hover', backgroundImage: 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'background.paper', backgroundImage: 'none',
                              borderColor: alpha(primaryHex, 0.35),
                            }
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                            Cost Breakdown
                          </Typography>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper
                                elevation={0}
                                sx={{
                                  textAlign: 'center',
                                  p: 1.5,
                                  bgcolor: 'action.hover', backgroundImage: 'none',
                                  borderRadius: 2,
                                  border: '1px solid', borderColor: 'divider',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: 'background.paper', backgroundImage: 'none',
                                    borderColor: alpha(theme.palette.success.main, 0.35),
                                  }
                                }}
                              >
                                <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                                  {formatCurrency(billingCalculation.base_price || 0)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Base Price
                                </Typography>
                              </Paper>
                            </Grid>
                            
                            <Grid item xs={12} sm={6} md={3}>
                              <Paper
                                elevation={0}
                                sx={{
                                  textAlign: 'center',
                                  p: 1.5,
                                  bgcolor: 'action.hover', backgroundImage: 'none',
                                  borderRadius: 2,
                                  border: '1px solid', borderColor: 'divider',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: 'background.paper', backgroundImage: 'none',
                                    borderColor: alpha(theme.palette.warning.main, 0.35),
                                  }
                                }}
                              >
                                <Typography variant="h6" sx={{ color: 'warning.main', fontWeight: 600 }}>
                                  {formatCurrency(billingCalculation.per_user_cost || 0)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  User Costs
                                </Typography>
                              </Paper>
                            </Grid>
                            
                            {billingCalculation.discount_amount > 0 && (
                              <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    textAlign: 'center',
                                    p: 1.5,
                                    bgcolor: 'action.hover', backgroundImage: 'none',
                                    borderRadius: 2,
                                    border: '1px solid', borderColor: 'divider',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: 'background.paper', backgroundImage: 'none',
                                      borderColor: alpha(theme.palette.success.main, 0.35),
                                    }
                                  }}
                                >
                                  <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    -{formatCurrency(billingCalculation.discount_amount || 0)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Discount ({billingCalculation.discount_percentage}%)
                                  </Typography>
                                </Paper>
                              </Grid>
                            )}
                            
                            <Grid item xs={12} sm={6} md={billingCalculation.discount_amount > 0 ? 3 : 6}>
                              <Paper
                                elevation={0}
                                sx={{
                                  textAlign: 'center',
                                  p: 1.5,
                                  bgcolor: alpha(theme.palette.success.main, 0.12),
                                  borderRadius: 2,
                                  border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.success.main, 0.2),
                                    borderColor: alpha(theme.palette.success.main, 0.5),
                                  }
                                }}
                              >
                                <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                                  ${billingCalculation.total_monthly_cost?.toFixed(2) || '0.00'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  Total Monthly
                                </Typography>
                              </Paper>
                            </Grid>
                          </Grid>
                          
                          {billingCalculation.override_active && (
                            <Alert severity="info" sx={{ mt: 2, bgcolor: alpha(theme.palette.info.main, 0.08), border: `1px solid ${alpha(theme.palette.info.main, 0.25)}` }}>
                              <Typography variant="body2">
                                <strong>Custom pricing override is active</strong> - This calculation uses custom pricing instead of the standard tier pricing.
                              </Typography>
                            </Alert>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                        No billing data available
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                        This company may need a billing account setup
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}

            {/* Subscription Status Section - Only show for existing companies */}
            {mode === 'edit' && formData.id && (
              <Grid item xs={12}>
                <Divider sx={{ my: 3, bgcolor: 'divider' }} />
                <Paper
                  elevation={0}
                  sx={{
                  p: 3,
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper', backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover', backgroundImage: 'none',
                    borderColor: alpha(primaryHex, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                    mb: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MonetizationIcon sx={{ color: 'primary.main', mr: 1, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        Subscription Status
                      </Typography>
                    </Box>
                    {subscriptionData?.billing_account?.stripe_subscription_id && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          if (!formData.id) return
                          try {
                            const result = await billingService.syncBillingAccount(formData.id)
                            if (result.success) {
                              await loadSubscriptionData() // Refresh data after sync
                            } else {
                              console.error('Failed to sync billing account:', result.error)
                            }
                          } catch (error) {
                            console.error('Error syncing billing account:', error)
                          }
                        }}
                        sx={{
                          color: 'primary.main',
                          borderColor: alpha(primaryHex, 0.5),
                          '&:hover': {
                            backgroundColor: alpha(primaryHex, 0.1),
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                        Sync with Stripe
                      </Button>
                    )}
                  </Box>
                  
                  {loadingSubscriptionData ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                      <CircularProgress sx={{ color: 'primary.main', mr: 2 }} />
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Loading subscription data...
                      </Typography>
                    </Box>
                  ) : subscriptionData ? (
                    <Grid container spacing={3}>
                      {/* Subscription Status Card */}
                      <Grid item xs={12} md={6}>
                        <Box sx={companyDialogInsetCardSx()}>
                          <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                            Current Status
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Subscription Status
                              </Typography>
                              <Chip 
                                label={getSubscriptionStatusLabel(subscriptionData.billing_account?.status || 'inactive')} 
                                size="small" 
                                sx={subscriptionStatusChipSx(
                                  theme,
                                  subscriptionData.billing_account?.status || 'inactive',
                                )}
                              />
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Payment Status
                              </Typography>
                              <Chip 
                                label={(() => {
                                  // Check if this is a free tier first
                                  if (billingCalculation && isFreeTierCalculation(billingCalculation)) {
                                    return 'Free Tier'
                                  }
                                  
                                  const paymentStatus = subscriptionData.billing_account?.payment_status
                                  if (paymentStatus === 'trial' || paymentStatus === 'trialing') return 'Trial'
                                  if (paymentStatus === 'paid') return 'Paid'
                                  if (paymentStatus === 'overdue') return 'Overdue'
                                  if (paymentStatus === 'pending') return 'Pending'
                                  if (paymentStatus === 'failed') return 'Failed'
                                  return paymentStatus ? paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1) : 'Unknown'
                                })()} 
                                size="small" 
                                sx={paymentStatusDisplayChipSx(
                                  theme,
                                  subscriptionData.billing_account?.payment_status,
                                  !!(billingCalculation && isFreeTierCalculation(billingCalculation)),
                                )}
                              />
                            </Box>
                            
                            {subscriptionData.billing_account?.stripe_subscription_id && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Stripe Subscription
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'primary.main', fontFamily: 'monospace' }}>
                                  {subscriptionData.billing_account.stripe_subscription_id.substring(0, 12)}...
                                </Typography>
                              </Box>
                            )}
                            
                            {subscriptionData.billing_account?.created_at && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Customer Since
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                  {new Date(subscriptionData.billing_account.created_at).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      
                      {/* Payment Method Card */}
                      <Grid item xs={12} md={6}>
                        <Box sx={companyDialogInsetCardSx()}>
                          <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                            Payment Method
                          </Typography>
                          
                          {subscriptionData.payment_methods && subscriptionData.payment_methods.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {subscriptionData.payment_methods.slice(0, 2).map((pm: any, index: number) => (
                                <Box
                                  key={index}
                                  sx={{
                                    ...companyDialogNestedPaperSx(),
                                    border: '1px solid',
                                    borderColor: pm.is_default
                                      ? alpha(theme.palette.success.main, 0.35)
                                      : 'divider',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                      {pm.type === 'card' ? 'Credit Card' : 'Bank Account'}
                                    </Typography>
                                    {pm.is_default && (
                                      <Chip 
                                        label="Default" 
                                        size="small" 
                                        sx={{ 
                                          bgcolor: alpha(theme.palette.success.main, 0.12),
                                          color: 'success.main',
                                          fontWeight: 500,
                                          border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                                        }} 
                                      />
                                    )}
                                  </Box>
                                  
                                  {pm.card && (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      {pm.card.brand.toUpperCase()} •••• {pm.card.last4}
                                    </Typography>
                                  )}
                                  
                                  {pm.us_bank_account && (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      {pm.us_bank_account.bank_name || 'Bank'} •••• {pm.us_bank_account.last4}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                No payment methods on file
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                        No subscription data available
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                        This company may not have a billing account set up
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}

            {/* Subscription Timeline Section - Only show for existing companies with subscription data */}
            {mode === 'edit' && formData.id && subscriptionData?.billing_account && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                  p: 3,
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper', backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover', backgroundImage: 'none',
                    borderColor: alpha(primaryHex, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                    mb: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <MonetizationIcon sx={{ color: 'primary.main', mr: 1, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      Subscription Timeline
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={3}>
                      {/* Subscription Duration */}
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'action.hover', backgroundImage: 'none',
                            textAlign: 'center',
                            height: '100%',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: 'background.paper', backgroundImage: 'none',
                              borderColor: alpha(primaryHex, 0.35),
                            }
                          }}
                        >
                        <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                          Customer Duration
                        </Typography>
                        
                        {subscriptionData.billing_account.created_at ? (
                          <>
                            <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 'bold', mb: 1 }}>
                              {(() => {
                                const startDate = new Date(subscriptionData.billing_account.created_at)
                                const now = new Date()
                                const diffTime = Math.abs(now.getTime() - startDate.getTime())
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                                
                                if (diffDays < 30) {
                                  return `${diffDays} days`
                                } else if (diffDays < 365) {
                                  const months = Math.floor(diffDays / 30)
                                  return `${months} month${months > 1 ? 's' : ''}`
                                } else {
                                  const years = Math.floor(diffDays / 365)
                                  const remainingMonths = Math.floor((diffDays % 365) / 30)
                                  return `${years} year${years > 1 ? 's' : ''}${remainingMonths > 0 ? ` ${remainingMonths}m` : ''}`
                                }
                              })()}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Since {new Date(subscriptionData.billing_account.created_at).toLocaleDateString()}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                            No start date available
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                    
                    {/* Next Billing Date */}
                    <Grid item xs={12} md={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: '1px solid', borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: 'action.hover', backgroundImage: 'none',
                          textAlign: 'center',
                          height: '100%',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'background.paper', backgroundImage: 'none',
                            borderColor: alpha(primaryHex, 0.35),
                          }
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                          Next Billing Date
                        </Typography>
                        
                        {subscriptionData.billing_account.next_billing_date ? (
                          <>
                            <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 'bold', mb: 1 }}>
                              {new Date(subscriptionData.billing_account.next_billing_date).getDate()}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {new Date(subscriptionData.billing_account.next_billing_date).toLocaleDateString('en-US', { 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
                              {(() => {
                                const nextBilling = new Date(subscriptionData.billing_account.next_billing_date)
                                const now = new Date()
                                const diffTime = nextBilling.getTime() - now.getTime()
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                                
                                if (diffDays < 0) {
                                  return `${Math.abs(diffDays)} days overdue`
                                } else if (diffDays === 0) {
                                  return 'Due today'
                                } else if (diffDays === 1) {
                                  return 'Due tomorrow'
                                } else {
                                  return `In ${diffDays} days`
                                }
                              })()}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                            No billing date set
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                    
                    {/* Last Payment */}
                    <Grid item xs={12} md={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: '1px solid', borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: 'action.hover', backgroundImage: 'none',
                          textAlign: 'center',
                          height: '100%',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'background.paper', backgroundImage: 'none',
                            borderColor: alpha(primaryHex, 0.35),
                          }
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                          Last Payment
                        </Typography>
                        
                        {subscriptionData.billing_account.last_payment_date ? (
                          <>
                            <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 'bold', mb: 1 }}>
                              ${subscriptionData.billing_account.last_payment_amount?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {new Date(subscriptionData.billing_account.last_payment_date).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 1 }}>
                              {(() => {
                                const lastPayment = new Date(subscriptionData.billing_account.last_payment_date)
                                const now = new Date()
                                const diffTime = now.getTime() - lastPayment.getTime()
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                                
                                if (diffDays === 0) {
                                  return 'Today'
                                } else if (diffDays === 1) {
                                  return 'Yesterday'
                                } else if (diffDays < 30) {
                                  return `${diffDays} days ago`
                                } else {
                                  const months = Math.floor(diffDays / 30)
                                  return `${months} month${months > 1 ? 's' : ''} ago`
                                }
                              })()}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                            No payment history
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Trial Management Section - Only show for existing companies and global admins */}
            {isGlobalAdmin && mode === 'edit' && formData.id && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                  p: 3,
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper', backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover', backgroundImage: 'none',
                    borderColor: alpha(primaryHex, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <MonetizationIcon sx={{ color: 'primary.main', mr: 1, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      Trial Management
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={3}>
                    {/* Trial Status Overview */}
                    <Grid item xs={12} md={8}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: '1px solid', borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: 'action.hover', backgroundImage: 'none',
                          height: '100%',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'background.paper', backgroundImage: 'none',
                            borderColor: alpha(primaryHex, 0.35),
                          }
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                          Trial Status
                        </Typography>
                        
                        {subscriptionData?.billing_account?.trial_ends_at ? (() => {
                          const trialEnd = new Date(subscriptionData.billing_account.trial_ends_at)
                          const diffDays = Math.ceil(
                            (trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
                          )
                          const statusLabel =
                            diffDays < 0 ? 'Expired' : diffDays === 0 ? 'Expires Today' : 'Active'
                          const daysLabel =
                            diffDays < 0
                              ? `${Math.abs(diffDays)} days ago`
                              : diffDays === 0
                                ? 'Today'
                                : `${diffDays} days`
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Status
                                </Typography>
                                <Chip
                                  label={statusLabel}
                                  size="small"
                                  sx={trialUrgencyChipSx(theme, diffDays)}
                                />
                              </Box>

                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Trial Ends
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                  {trialEnd.toLocaleDateString()}
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Days Remaining
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: trialUrgencyAccentColor(theme, diffDays),
                                    fontWeight: 500,
                                  }}
                                >
                                  {daysLabel}
                                </Typography>
                              </Box>

                              {subscriptionData?.billing_account?.status && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Subscription Status
                                  </Typography>
                                  <Chip
                                    label={subscriptionData.billing_account.status
                                      .charAt(0)
                                      .toUpperCase() + subscriptionData.billing_account.status.slice(1)}
                                    size="small"
                                    sx={rawSubscriptionAccountStatusChipSx(
                                      theme,
                                      subscriptionData.billing_account.status,
                                    )}
                                  />
                                </Box>
                              )}
                            </Box>
                          )
                        })() : (
                          <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                              No trial information available
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                    
                    {/* Create New Trial */}
                    <Grid item xs={12} md={4}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          border: '1px solid', borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: 'action.hover', backgroundImage: 'none',
                          height: '100%',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'background.paper', backgroundImage: 'none',
                            borderColor: alpha(primaryHex, 0.35),
                          }
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                          Create Trial
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {[7, 15, 30].map((days) => (
                              <Button
                                key={days}
                                variant="outlined"
                                size="small"
                                onClick={() => createTrialSubscription(days)}
                                disabled={loadingTrial}
                                sx={{
                                  color: 'primary.main',
                                  borderColor: alpha(primaryHex, 0.5),
                                  '&:hover': {
                                    backgroundColor: alpha(primaryHex, 0.1),
                                    borderColor: 'primary.main'
                                  },
                                  '&:disabled': {
                                    color: 'text.disabled',
                                    borderColor: 'divider'
                                  }
                                }}
                              >
                                {days} Days
                              </Button>
                            ))}
                          </Box>
                          
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            Creates a free trial with current billing tier
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Billing Information Section - Only show for existing companies */}
            {mode === 'edit' && formData.id && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                  p: 3,
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper', backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover', backgroundImage: 'none',
                    borderColor: alpha(primaryHex, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                    mb: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MonetizationIcon sx={{ color: 'primary.main', mr: 1, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        Billing Information
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!subscriptionData?.billing_account?.stripe_customer_id && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setCreateCustomerFormData({
                              name: formData.name || '',
                              email: '',
                              address: {
                                line1: '',
                                line2: '',
                                city: '',
                                state: '',
                                postal_code: '',
                                country: ''
                              }
                            })
                            setShowCreateCustomerForm(true)
                          }}
                          sx={{
                            bgcolor: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            },
                          }}
                        >
                          Create Customer
                        </Button>
                      )}
                      {!editingBillingInfo && subscriptionData?.billing_account?.stripe_customer_id && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleBillingInfoEdit}
                          sx={{
                            color: 'primary.main',
                            borderColor: alpha(primaryHex, 0.5),
                            '&:hover': {
                              backgroundColor: alpha(primaryHex, 0.1),
                              borderColor: 'primary.main'
                            }
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  </Box>
                  
                  {loadingBillingInfo ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                      <CircularProgress sx={{ color: 'primary.main', mr: 2 }} />
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Loading billing information from Stripe...
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      {/* Show message if no Stripe customer ID */}
                      {!subscriptionData?.billing_account?.stripe_customer_id && (
                        <Alert severity="info" sx={{ mb: 3, bgcolor: alpha(theme.palette.info.main, 0.08), border: `1px solid ${alpha(theme.palette.info.main, 0.25)}` }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            <strong>No Stripe customer account found</strong><br/>
                            Create a Stripe customer account to manage billing information for this company.
                          </Typography>
                        </Alert>
                      )}

                      {/* Create Customer Form */}
                      {showCreateCustomerForm && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            border: `1px solid ${alpha(primaryHex, 0.3)}`,
                            borderRadius: 2,
                            backgroundColor: alpha(primaryHex, 0.1),
                            mb: 3
                          }}
                        >
                          <Typography variant="h6" sx={{ color: 'text.primary', mb: 3, fontWeight: 600 }}>
                            Create Stripe Customer
                          </Typography>
                          
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    Customer Name *
                                  </Typography>
                                  <TextField
                                    size="small"
                                    value={createCustomerFormData.name}
                                    onChange={(e) => setCreateCustomerFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Company or contact name"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        color: 'text.primary',
                                        '& fieldset': { borderColor: 'divider' },
                                        '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                        '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                      }
                                    }}
                                  />
                                </Box>
                                
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    Email Address *
                                  </Typography>
                                  <TextField
                                    size="small"
                                    type="email"
                                    value={createCustomerFormData.email}
                                    onChange={(e) => setCreateCustomerFormData(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="billing@company.com"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        color: 'text.primary',
                                        '& fieldset': { borderColor: 'divider' },
                                        '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                        '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                      }
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    Street Address
                                  </Typography>
                                  <TextField
                                    size="small"
                                    value={createCustomerFormData.address.line1}
                                    onChange={(e) => setCreateCustomerFormData(prev => ({ 
                                      ...prev, 
                                      address: { ...prev.address, line1: e.target.value }
                                    }))}
                                    placeholder="123 Main Street"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        color: 'text.primary',
                                        '& fieldset': { borderColor: 'divider' },
                                        '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                        '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                      }
                                    }}
                                  />
                                </Box>
                                
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    City, State, Postal Code
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      size="small"
                                      placeholder="City"
                                      value={createCustomerFormData.address.city}
                                      onChange={(e) => setCreateCustomerFormData(prev => ({ 
                                        ...prev, 
                                        address: { ...prev.address, city: e.target.value }
                                      }))}
                                      sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                          color: 'text.primary',
                                          '& fieldset': { borderColor: 'divider' },
                                          '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                          '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                        }
                                      }}
                                    />
                                    <TextField
                                      size="small"
                                      placeholder="State"
                                      value={createCustomerFormData.address.state}
                                      onChange={(e) => setCreateCustomerFormData(prev => ({ 
                                        ...prev, 
                                        address: { ...prev.address, state: e.target.value }
                                      }))}
                                      sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                          color: 'text.primary',
                                          '& fieldset': { borderColor: 'divider' },
                                          '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                          '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                        }
                                      }}
                                    />
                                    <TextField
                                      size="small"
                                      placeholder="Postal Code"
                                      value={createCustomerFormData.address.postal_code}
                                      onChange={(e) => setCreateCustomerFormData(prev => ({ 
                                        ...prev, 
                                        address: { ...prev.address, postal_code: e.target.value }
                                      }))}
                                      sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                          color: 'text.primary',
                                          '& fieldset': { borderColor: 'divider' },
                                          '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                          '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                        }
                                      }}
                                    />
                                  </Box>
                                </Box>
                                
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    Country
                                  </Typography>
                                  <TextField
                                    size="small"
                                    value={createCustomerFormData.address.country}
                                    onChange={(e) => setCreateCustomerFormData(prev => ({ 
                                      ...prev, 
                                      address: { ...prev.address, country: e.target.value }
                                    }))}
                                    placeholder="United States"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        color: 'text.primary',
                                        '& fieldset': { borderColor: 'divider' },
                                        '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                        '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                      }
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                            <Button
                              variant="outlined"
                              onClick={handleCreateCustomerCancel}
                              disabled={creatingCustomer}
                              sx={{
                                color: 'text.secondary',
                                borderColor: 'divider',
                                '&:hover': {
                                  backgroundColor: alpha(primaryHex, 0.1),
                                  borderColor: alpha(primaryHex, 0.5)
                                }
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleCreateCustomer}
                              disabled={creatingCustomer || !createCustomerFormData.name || !createCustomerFormData.email}
                              sx={{
                                bgcolor: 'primary.main',
                                '&:hover': {
                                  bgcolor: 'primary.dark',
                                },
                                '&:disabled': {
                                  bgcolor: alpha(primaryHex, 0.3),
                                },
                              }}
                            >
                              {creatingCustomer ? (
                                <CircularProgress size={20} sx={{ color: 'text.primary' }} />
                              ) : (
                                'Create Customer'
                              )}
                            </Button>
                          </Box>
                        </Paper>
                      )}
                      
                      {subscriptionData?.billing_account?.stripe_customer_id && (
                        <Grid container spacing={3}>
                          {/* Basic Information */}
                          <Grid item xs={12} md={6}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                border: '1px solid', borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'action.hover', backgroundImage: 'none',
                                height: '100%',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: 'background.paper', backgroundImage: 'none',
                                  borderColor: alpha(primaryHex, 0.35),
                                }
                              }}
                            >
                              <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                                Basic Information
                              </Typography>
                              
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    Name
                                  </Typography>
                                  {editingBillingInfo ? (
                                    <TextField
                                      size="small"
                                      value={billingFormData.name}
                                      onChange={(e) => setBillingFormData(prev => ({ ...prev, name: e.target.value }))}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          color: 'text.primary',
                                          '& fieldset': { borderColor: 'divider' },
                                          '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                          '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {billingInfo?.name || 'Not set'}
                                    </Typography>
                                  )}
                                </Box>
                                
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    Email
                                  </Typography>
                                  {editingBillingInfo ? (
                                    <TextField
                                      size="small"
                                      type="email"
                                      value={billingFormData.email}
                                      onChange={(e) => setBillingFormData(prev => ({ ...prev, email: e.target.value }))}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          color: 'text.primary',
                                          '& fieldset': { borderColor: 'divider' },
                                          '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                          '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {billingInfo?.email || 'Not set'}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                          
                          {/* Address Information */}
                          <Grid item xs={12} md={6}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                border: '1px solid', borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'action.hover', backgroundImage: 'none',
                                height: '100%',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: 'background.paper', backgroundImage: 'none',
                                  borderColor: alpha(primaryHex, 0.35),
                                }
                              }}
                            >
                              <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                                Address
                              </Typography>
                              
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    Street Address
                                  </Typography>
                                  {editingBillingInfo ? (
                                    <TextField
                                      size="small"
                                      value={billingFormData.address.line1}
                                      onChange={(e) => setBillingFormData(prev => ({ 
                                        ...prev, 
                                        address: { ...prev.address, line1: e.target.value }
                                      }))}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          color: 'text.primary',
                                          '& fieldset': { borderColor: 'divider' },
                                          '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                          '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {billingInfo?.address?.line1 || 'Not set'}
                                    </Typography>
                                  )}
                                </Box>
                                
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    City, State, Postal Code
                                  </Typography>
                                  {editingBillingInfo ? (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <TextField
                                        size="small"
                                        placeholder="City"
                                        value={billingFormData.address.city}
                                        onChange={(e) => setBillingFormData(prev => ({ 
                                          ...prev, 
                                          address: { ...prev.address, city: e.target.value }
                                        }))}
                                        sx={{
                                          flex: 1,
                                          '& .MuiOutlinedInput-root': {
                                            color: 'text.primary',
                                            '& fieldset': { borderColor: 'divider' },
                                            '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                            '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                          }
                                        }}
                                      />
                                      <TextField
                                        size="small"
                                        placeholder="State"
                                        value={billingFormData.address.state}
                                        onChange={(e) => setBillingFormData(prev => ({ 
                                          ...prev, 
                                          address: { ...prev.address, state: e.target.value }
                                        }))}
                                        sx={{
                                          flex: 1,
                                          '& .MuiOutlinedInput-root': {
                                            color: 'text.primary',
                                            '& fieldset': { borderColor: 'divider' },
                                            '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                            '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                          }
                                        }}
                                      />
                                      <TextField
                                        size="small"
                                        placeholder="Postal Code"
                                        value={billingFormData.address.postal_code}
                                        onChange={(e) => setBillingFormData(prev => ({ 
                                          ...prev, 
                                          address: { ...prev.address, postal_code: e.target.value }
                                        }))}
                                        sx={{
                                          flex: 1,
                                          '& .MuiOutlinedInput-root': {
                                            color: 'text.primary',
                                            '& fieldset': { borderColor: 'divider' },
                                            '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                            '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                          }
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {[billingInfo?.address?.city, billingInfo?.address?.state, billingInfo?.address?.postal_code]
                                        .filter(Boolean).join(', ') || 'Not set'}
                                    </Typography>
                                  )}
                                </Box>
                                
                                <Box>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                    Country
                                  </Typography>
                                  {editingBillingInfo ? (
                                    <TextField
                                      size="small"
                                      value={billingFormData.address.country}
                                      onChange={(e) => setBillingFormData(prev => ({ 
                                        ...prev, 
                                        address: { ...prev.address, country: e.target.value }
                                      }))}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          color: 'text.primary',
                                          '& fieldset': { borderColor: 'divider' },
                                          '&:hover fieldset': { borderColor: alpha(primaryHex, 0.35) },
                                          '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                      {billingInfo?.address?.country || 'Not set'}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                          
                          {/* Tax Information */}
                          <Grid item xs={12}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                border: '1px solid', borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'action.hover', backgroundImage: 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: 'background.paper', backgroundImage: 'none',
                                  borderColor: alpha(primaryHex, 0.35),
                                }
                              }}
                            >
                              <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
                                Tax Information (VAT)
                              </Typography>
                              
                              {billingInfo?.tax_ids && billingInfo.tax_ids.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {billingInfo.tax_ids.map((taxId: any, index: number) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Chip
                                        label={`${taxId.type.toUpperCase()}: ${taxId.value}`}
                                        size="small"
                                        sx={{
                                          bgcolor: alpha(theme.palette.success.main, 0.12),
                                          color: 'success.main',
                                          border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                                        }}
                                      />
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                                  No tax IDs on file
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  )}
                  
                  {/* Edit Actions */}
                  {editingBillingInfo && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={handleBillingInfoCancel}
                        disabled={loadingBillingInfo}
                        sx={{
                          color: 'text.secondary',
                          borderColor: 'divider',
                          '&:hover': {
                            backgroundColor: alpha(primaryHex, 0.1),
                            borderColor: alpha(primaryHex, 0.5)
                          }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleBillingInfoSave}
                        disabled={loadingBillingInfo}
                        sx={{
                          bgcolor: 'primary.main',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '&:disabled': {
                            bgcolor: alpha(primaryHex, 0.3),
                          },
                        }}
                      >
                        {loadingBillingInfo ? (
                          <CircularProgress size={20} sx={{ color: 'text.primary' }} />
                        ) : (
                          'Save Changes'
                        )}
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}

            {/* Subscription History Section - Only show for existing companies */}
            {mode === 'edit' && formData.id && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                  p: 3,
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper', backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover', backgroundImage: 'none',
                    borderColor: alpha(primaryHex, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                    mb: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <MonetizationIcon sx={{ color: 'primary.main', mr: 1, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      Subscription & Trial History
                    </Typography>
                  </Box>
                  
                  {loadingHistory ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                      <CircularProgress sx={{ color: 'primary.main', mr: 2 }} />
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Loading subscription history...
                      </Typography>
                    </Box>
                  ) : subscriptionHistory.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {subscriptionHistory.map((event, index) => (
                        <Paper
                          key={event.id || index}
                          elevation={0}
                          sx={subscriptionHistoryRowPaperSx(theme)}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={(() => {
                                  switch (event.type) {
                                    case 'trial_started': return 'Trial Started'
                                    case 'trial_ended': return 'Trial Ended'
                                    case 'subscription_created': return 'Subscription Created'
                                    case 'payment': return 'Payment'
                                    case 'invoice': return 'Invoice'
                                    case 'billing_period_started': return 'Billing Period'
                                    default: return event.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                  }
                                })()}
                                size="small"
                                sx={subscriptionHistoryEventChipSx(theme, event.type)}
                              />
                              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                                {event.source}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {new Date(event.date).toLocaleDateString()} {new Date(event.date).toLocaleTimeString()}
                            </Typography>
                          </Box>
                          
                          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, mb: 1 }}>
                            {event.description}
                          </Typography>
                          
                          {event.amount > 0 && (
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                              ${event.amount.toFixed(2)}
                            </Typography>
                          )}
                          
                          {event.trial_end_date && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Trial ended: {new Date(event.trial_end_date).toLocaleDateString()}
                            </Typography>
                          )}
                          
                          {event.period_end_date && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Period ends: {new Date(event.period_end_date).toLocaleDateString()}
                            </Typography>
                          )}
                          
                          {event.stripe_invoice_id && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                              <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace' }}>
                                {event.stripe_invoice_id}
                              </Typography>
                              {(event.type === 'payment' || event.type === 'invoice') && (
                                <Tooltip title="Download Invoice PDF">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadInvoice(event.stripe_invoice_id)}
                                    disabled={downloadingInvoices.has(event.stripe_invoice_id)}
                                    sx={{
                                      color: 'primary.main',
                                      '&:hover': {
                                        backgroundColor: alpha(primaryHex, 0.1)
                                      },
                                      '&:disabled': {
                                        color: 'text.disabled'
                                      }
                                    }}
                                  >
                                    {downloadingInvoices.has(event.stripe_invoice_id) ? (
                                      <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
                        No subscription history found
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                        History will appear here once the company has billing activity
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}

            {/* Billing History Section - Only show for existing companies */}
            {mode === 'edit' && formData.id && billingHistory.length > 0 && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                  p: 3,
                  border: '1px solid', borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.paper', backgroundImage: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover', backgroundImage: 'none',
                    borderColor: alpha(primaryHex, 0.35),
                    boxShadow: theme.shadows[4],
                  },
                    mb: 3
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <MonetizationIcon sx={{ color: 'primary.main', mr: 1, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                      Recent Billing History
                    </Typography>
                  </Box>
                  
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      border: '1px solid', borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'action.hover', backgroundImage: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'background.paper', backgroundImage: 'none',
                        borderColor: alpha(primaryHex, 0.35),
                      }
                    }}
                  >
                    <Grid container spacing={2}>
                      {billingHistory.slice(0, 5).map((invoice: any, index: number) => (
                        <Grid item xs={12} key={index}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              border: '1px solid', borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'background.paper', backgroundImage: 'none',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'action.hover', backgroundImage: 'none',
                                borderColor: alpha(primaryHex, 0.35),
                              }
                            }}
                          >
                            <Box>
                              <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                                {invoice.description || `Invoice ${invoice.id?.substring(0, 8)}...`}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {new Date(invoice.created * 1000).toLocaleDateString()}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                                ${(invoice.amount_paid / 100).toFixed(2)}
                              </Typography>
                              <Chip 
                                label={invoice.status === 'paid' ? 'Paid' : invoice.status} 
                                size="small" 
                                sx={{ 
                                  bgcolor:
                                    invoice.status === 'paid'
                                      ? alpha(theme.palette.success.main, 0.12)
                                      : alpha(theme.palette.warning.main, 0.12),
                                  color: invoice.status === 'paid' ? 'success.main' : 'warning.main',
                                  fontWeight: 500,
                                  border:
                                    invoice.status === 'paid'
                                      ? `1px solid ${alpha(theme.palette.success.main, 0.35)}`
                                      : `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
                                }} 
                              />
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                    
                    {billingHistory.length > 5 && (
                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                          Showing 5 of {billingHistory.length} recent invoices
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Paper>
              </Grid>
            )}

            {/* Billing Override Manager for Global Admins */}
            {isGlobalAdmin && mode === 'edit' && formData.id && (
              <Grid item xs={12}>
                <Divider sx={{ my: 3, bgcolor: 'divider' }} />
                <BillingOverrideManager
                  companyId={formData.id}
                  companyName={formData.name || 'Unknown Company'}
                  isGlobalAdmin={isGlobalAdmin}
                  onBillingUpdate={() => {
                    // Refresh billing calculation and subscription data when override is updated
                    loadBillingCalculation()
                    loadSubscriptionData()
                  }}
                />
              </Grid>
            )}
          </Grid>
        </TabPanel>
        )}

        {/* Features Tab */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                Platform Features
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Enable or disable features for your company's platform
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.features?.mapEditor || false}
                    onChange={() => handleFeatureToggle('mapEditor')}
                  />
                }
                label="Map Editor"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                Allow users to create and edit custom maps
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.features?.chat || false}
                    onChange={() => handleFeatureToggle('chat')}
                  />
                }
                label="Chat System"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                Enable real-time chat functionality
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.features?.whiteboard || false}
                    onChange={() => handleFeatureToggle('whiteboard')}
                  />
                }
                label="Whiteboard"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                Collaborative whiteboard tools
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.features?.teleport || false}
                    onChange={() => handleFeatureToggle('teleport')}
                  />
                }
                label="Teleport"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                Let users teleport directly into a room
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.features?.screenSharing || false}
                    onChange={() => handleFeatureToggle('screenSharing')}
                  />
                }
                label="Screen Sharing"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                Allow users to share their screens
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.features?.videoCalling || false}
                    onChange={() => handleFeatureToggle('videoCalling')}
                  />
                }
                label="Video Calling"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                Enable video conferencing
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.features?.customBranding || false}
                    onChange={() => handleFeatureToggle('customBranding')}
                  />
                }
                label="Custom Branding"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                Allow custom logos and branding
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.features?.advancedAnalytics || false}
                    onChange={() => handleFeatureToggle('advancedAnalytics')}
                  />
                }
                label="Advanced Analytics"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                Detailed usage analytics and reports
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.features?.apiAccess || false}
                    onChange={() => handleFeatureToggle('apiAccess')}
                  />
                }
                label="API Access"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                REST API access for integrations
              </Typography>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Domain Management Tab */}
        <TabPanel value={activeTab} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                Allowed Domains
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                Add domains that are allowed to access your platform.
              </Typography>
              {formData.allowed_domains && formData.allowed_domains.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {formData.allowed_domains.map((domain, index) => (
                    <Chip
                      key={index}
                      label={domain}
                      onDelete={() => handleRemoveDomain(domain)}
                      sx={{ backgroundColor: alpha(primaryHex, 0.1), color: 'primary.main' }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  No allowed domains added yet.
                </Typography>
              )}
              <TextField
                fullWidth
                label="Add New Domain"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                margin="dense"
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleAddDomain}>
                      <AddIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.auto_assignment_enabled || false}
                    onChange={(e) => handleInputChange('auto_assignment_enabled', e.target.checked)}
                  />
                }
                label="Sync existing accounts on these domains"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                New sign-ups with an email on an allowed domain always join this company. This only decides
                whether "Sync users" also brings in existing accounts on these domains that have no company.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.matrix_chat_enabled || false}
                    onChange={(e) => handleInputChange('matrix_chat_enabled', e.target.checked)}
                  />
                }
                label="Enable Matrix Chat"
                sx={{ color: 'text.primary' }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', ml: 4 }}>
                Use the Matrix-backed chat for this company instead of the default chat. Only enable once the company's chat tenant is provisioned.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="dense"
                label="Matrix Chat Domain"
                value={formData.matrix_chat_domain || ''}
                onChange={(e) => handleInputChange('matrix_chat_domain', e.target.value)}
                helperText="Tenant slug for the chat homeserver, e.g. openprovider → openprovider.sevv.chat. Required when Matrix Chat is enabled."
              />
            </Grid>

            {canPickDefaultRole && (
            <Grid item xs={12}>
              <FormControl fullWidth margin="dense">
                <InputLabel sx={{ color: 'text.secondary' }}>Default Role for Auto-Assigned Users</InputLabel>
                <Select
                  value={defaultRolesLoading ? '' : defaultRoleKey}
                  onChange={(e) =>
                    handleInputChange('default_role', roleSendValue(String(e.target.value)))
                  }
                  disabled={defaultRolesLoading}
                  displayEmpty={defaultRolesLoading}
                  renderValue={
                    defaultRolesLoading
                      ? () => 'Loading roles...'
                      : (value) =>
                          defaultRoleChoices.find((c) => c.key === value)?.name ?? String(value)
                  }
                  label="Default Role for Auto-Assigned Users"
                  sx={{
                    color: 'text.primary',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(primaryHex, 0.35),
                    },
                  }}
                >
                  {defaultRoleChoices.map((choice) => (
                    <MenuItem key={choice.key} value={choice.key} disabled={choice.disabled}>
                      {choice.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            )}
          </Grid>
        </TabPanel>
      </StyledDialogContent>
      
      <StyledDialogActions>
        <StyledButton onClick={onClose} variant="outlined">
          Cancel
        </StyledButton>
        <StyledButton onClick={onSubmit} variant="contained">
          {mode === 'create' ? 'Create Company' : 'Update Company'}
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>

    {/* Billing Information Warning Dialog */}
    <Dialog
      open={showBillingWarning}
      onClose={() => setShowBillingWarning(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper', backgroundImage: 'none',
          border: '1px solid', borderColor: 'divider',
          borderRadius: 2,
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <DialogTitle sx={{ color: 'text.primary', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonetizationIcon sx={{ color: 'warning.main', fontSize: 24 }} />
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
            Edit Billing Information
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ py: 3 }}>
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 3,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
            '& .MuiAlert-icon': {
              color: 'warning.main'
            }
          }}
        >
          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500, mb: 1 }}>
            ⚠️ Important Warning
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            You are about to edit billing information for this company. This will update the customer's information in Stripe and may affect:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2, color: 'text.secondary' }}>
            <li>Future invoice generation</li>
            <li>Tax calculations</li>
            <li>Payment processing</li>
            <li>Customer records in Stripe</li>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, fontWeight: 500 }}>
            Please ensure all information is accurate before proceeding.
          </Typography>
        </Alert>
        
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Do you want to continue with editing the billing information?
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
        <Button
          onClick={() => setShowBillingWarning(false)}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: alpha(primaryHex, 0.1)
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleBillingWarningConfirm}
          variant="contained"
          color="warning"
        >
          Continue Editing
        </Button>
      </DialogActions>
    </Dialog>
    </>
  )
} 